/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type {
  BaseDiagramProps,
  Bounds,
  DiagramLabelPosition,
  States,
  DiagramNodeData,
  DiagramLinkData,
  DiagramLayoutOutput,
  Dimensions
} from './diagram.types';
import type { TextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { DiagramLayers } from './DiagramLayers';
import { DiagramPanZoomContainer } from './DiagramPanZoomContainer';
import { useEvents } from './hooks/useEvents';
import { useDiagramDatatip } from './hooks/useDiagramDatatip';
import { useTextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { usePanZoom } from './hooks/usePanZoom';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { useUser } from '#hooks/UNSAFE_useUser';
import { useDiagramSelection } from './hooks/useDiagramSelection';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { Keys } from '#utils/UNSAFE_keys';
import {
  constructLayoutJSON,
  deconstructLayoutJSON,
  getFocusedItemBounds,
  mergeDimensions,
  isEqualBounds,
  getDimensionsFromBounds,
  getContentBounds,
  filterLinksWithNoStartOrEndNodes,
  getAllLabelBounds,
  createLinksMap,
  createNodesMap,
  getPromotedLinks,
  createPromotedLinksMap,
  linkPositionChanged
} from './utils/diagramUtils';
import { centerXYToPanXY } from './utils/panUtils';
import { outerStyles } from './themes/DiagramStyles.css';
import { useVisTouchResponse } from '#hooks/PRIVATE_useVisTouchResponse';
import { merge } from '@oracle/oraclejet-internal-utilities/stringUtils';
import { useDiagramContextMenu } from './hooks/useDiagramContextMenu';

type DiagramWithDimensionsProps<
  K1 extends string | number,
  K2 extends string | number,
  D1,
  D2,
  L
> = Omit<BaseDiagramProps<K1, K2, D1, D2, L>, 'width' | 'height'> & {
  width: number;
  height: number;
};

const emptyKeys = { all: false, keys: new Set() };

export function BaseDiagramWithDimensions<
  K1 extends string | number,
  K2 extends string | number,
  D1 extends DiagramNodeData<K1, D1>,
  D2 extends DiagramLinkData<K2, K1>,
  L extends DiagramLayoutOutput<K1, K2>
>({
  nodes = [],
  links = [],
  width,
  height,
  selectedIds = {},
  selectionMode = 'none',
  layout,
  nodeRenderer,
  linkRenderer,
  onSelectionChange,
  onItemHover,
  onItemFocus,
  highlightedIds = {},
  datatip,
  expanded = emptyKeys as Keys<K1>,
  onExpandedChange,
  contextMenuConfig,
  normalizeLinks,
  ...otherProps
}: DiagramWithDimensionsProps<K1, K2, D1, D2, L>) {
  const focusedItemBoundsRef = useRef<Bounds | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [bounds, setBounds] = useState<Map<K1, Bounds>>(new Map());
  const [absBounds, setAbsBounds] = useState<Map<K1, Bounds>>(new Map());
  const linkDimensions = useRef<Map<K2, Dimensions>>(new Map());
  const promotedLinkDimensions = useRef<Map<string, Dimensions>>(new Map());
  const [combinedLabelBounds, setCombinedLabelBounds] = useState<Bounds>();
  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  const diagramNodes = nodes;
  const nodeMap = createNodesMap<K1, D1>(diagramNodes, expanded);
  const nodeIdToParentMap = nodeMap.itemIdToParentMap;
  const nodeIdToDataMap = nodeMap.itemIdToDataMap;
  const diagramLinks = filterLinksWithNoStartOrEndNodes(links, nodeIdToDataMap);
  const linkMap = createLinksMap<K1, K2, D2>(diagramLinks);
  const linkIdToDataMap = linkMap.itemIdToDataMap;
  const resizeThrottling = useRef<boolean>(false);
  const newDimensions = getDimensionsFromBounds(bounds);
  const { textMeasureContent, getTextDimensions } = useTextDimensions();
  const labelDimensions = useRef<Map<K1 | K2, TextDimensions>>(new Map());
  const linkDimsChanged = useRef<boolean>(false);
  const linkRerenderNeeded = useRef<boolean>(false);
  const nodeLabelPosRef = useRef<Map<K1, DiagramLabelPosition>>(new Map());
  const linkLabelPosRef = useRef<Map<K2, DiagramLabelPosition>>(new Map());
  const [layoutOutput, setLayoutOutput] = useState<L>();
  const nodeBoundsRef = useRef<Map<K1, Bounds>>(new Map());
  const promotedLinks = getPromotedLinks<K1, K2, D1, D2>(
    links,
    expanded,
    nodeMap.allContainerNodes,
    nodeMap.allVisibleNodes
  );
  const promotedLinkMap = createPromotedLinksMap<K1, D2>(promotedLinks);
  const contentBounds = getContentBounds(bounds, combinedLabelBounds);
  const {
    panZoomProps,
    panProps,
    zoomProps,
    toggleButtonContent,
    cursor,
    centerX,
    centerY,
    zoomValue,
    kbPanCallback,
    kbZoomCallback
  } = usePanZoom({
    props: otherProps,
    captureTargetRef: rootRef,
    width: width,
    height: height,
    contentBounds,
    isRtl
  });

  const { touchResponse, touchResponseStyle } = useVisTouchResponse({
    type: 'auto',
    supportsTouchDragGestures: zoomProps.zooming === 'on' || panProps.panning !== 'off',
    rootRef
  });

  const { onContextMenuDismissed, focusedItemInfo, hoveredItemInfo, activeId, eventsProps } =
    useEvents<K1, K2, D1, D2>(
      touchResponse,
      diagramNodes[0],
      diagramNodes,
      diagramLinks,
      promotedLinks,
      absBounds,
      linkIdToDataMap,
      nodeIdToDataMap,
      width,
      height,
      otherProps.maxZoom || 1,
      expanded,
      nodeIdToParentMap,
      promotedLinkMap,
      onItemHover,
      onItemFocus,
      kbPanCallback,
      kbZoomCallback,
      onExpandedChange
    );

  const { selectionProps, selectionContent } = useDiagramSelection<K1, K2, D1, D2>({
    selectedIds,
    selectionMode,
    nodeMap,
    linkMap,
    focusedItemInfo,
    onSelectionChange,
    rootRef,
    promotedLinkMap
  });

  // This is for the datatip kb position
  if (focusedItemInfo && absBounds.size > 0) {
    focusedItemBoundsRef.current = getFocusedItemBounds(
      focusedItemInfo,
      absBounds,
      diagramLinks,
      linkMap.itemIdToIndexMap,
      rootRef
    );
  }

  const {
    datatipContent,
    datatipProps: { 'aria-describedby': datatipAriaDescribedby, ...datatipProps }
  } = useDiagramDatatip({
    touchResponse,
    datatip,
    focusedItemInfo,
    focusedItemBoundsRef,
    panState: centerXYToPanXY(centerX, centerY, zoomValue, width, height),
    zoom: zoomValue,
    width: width,
    nodesMap: nodeIdToDataMap,
    linksMap: linkIdToDataMap,
    hoveredItem: hoveredItemInfo
  });

  const [visibility, setVisibility] = useState<'visible' | 'hidden'>('hidden');
  const style = {
    width: width,
    height: height,
    cursor: cursor,
    visibility: visibility,
    ...touchResponseStyle
  };
  // When expanded changes, diagram loses focus so we need to call re-render
  const expandRerender = useRef(false);
  useLayoutEffect(() => {
    if (rootRef.current) {
        expandRerender.current = true
    }
  }, [expanded]);
  useLayoutEffect(() => {
    if (visibility === 'visible' && expandRerender.current == true) {
      rootRef.current?.focus();
      expandRerender.current = false;
    }
  }, [visibility]);
  const states = useRef<States<K1, K2, D2>>();

  states.current = {
    // update states to new state
    selectedNodeIds: selectedIds.nodes ?? [],
    selectedLinkIds: selectedIds.links ?? [],
    selectedPromotedLinkIds: selectedIds.promotedLinks ?? [],
    focusedId:
      focusedItemInfo.isCurrent && focusedItemInfo.isFocusVisible ? focusedItemInfo.id : undefined,
    hoveredId: hoveredItemInfo?.isCurrent ? hoveredItemInfo.id : undefined,
    activeId,
    highlightedNodeIds: highlightedIds.nodes ?? [],
    highlightedLinkIds: highlightedIds.links ?? [],
    highlightedPromotedLinkIds: highlightedIds.promotedLinks ?? [],
    zoom: zoomValue,
    expanded: expanded || { all: false, keys: new Set() }
  };

  const { contextMenuContent, contextMenuProps } = useDiagramContextMenu<K1, K2, D1, D2>({
    focusedItemInfo,
    rootRef,
    contextMenuConfig: nodes.length > 0 ? contextMenuConfig : undefined,
    onContextMenuDismissed,
    nodesMap: nodeIdToDataMap,
    linksMap: linkIdToDataMap,
    promotedLinkMap
  });

  const mergedEventProps = mergeProps(
    panZoomProps,
    eventsProps,
    selectionProps,
    datatipProps,
    contextMenuProps
  );

  // Called anytime node changes size
  const onNodeSizeChanged = (nodeWidth: number, nodeHeight: number, id: K1) => {
    newDimensions.set(id, { width: nodeWidth, height: nodeHeight });
    if (resizeThrottling.current) {
      return;
    }

    resizeThrottling.current = true;
    // performs layout at the next available animation frame when all measurements should be ready
    window.requestAnimationFrame(() => {
      const { labelDims, layoutJSON } = constructLayoutJSON<K1, K2, D1, D2>(
        nodeIdToDataMap,
        linkIdToDataMap,
        promotedLinks,
        newDimensions,
        width,
        height,
        zoomValue,
        nodeIdToParentMap,
        rootRef,
        getTextDimensions
      );
      labelDimensions.current = labelDims;
      const layoutOutput = layout(layoutJSON);
      const { nodePoints, nodeLabelPos, linkLabelPos } = deconstructLayoutJSON<K1, K2, D1, D2>(
        layoutOutput,
        diagramNodes,
        diagramLinks
      );
      nodeLabelPosRef.current = nodeLabelPos;
      linkLabelPosRef.current = linkLabelPos;
      const { nodeBounds, nodeAbsBounds } = mergeDimensions(newDimensions, nodePoints, rootRef);
      const labelBounds = getAllLabelBounds(
        labelDims,
        nodeLabelPos,
        linkLabelPos,
        direction === 'rtl',
        rootRef
      );
      const equalBounds = isEqualBounds(nodeBounds, bounds);
      if (!equalBounds || linkDimsChanged.current || linkRerenderNeeded.current) {
        linkRerenderNeeded.current = linkPositionChanged(nodeBounds, bounds, links);
        setBounds(nodeBounds);
        setAbsBounds(nodeAbsBounds)
        setCombinedLabelBounds(labelBounds);
        setLayoutOutput(layoutOutput);
        linkDimsChanged.current = false;
        setVisibility('hidden');
      } else if (visibility == 'hidden') {
        setVisibility('visible');
      }
      resizeThrottling.current = false;
    });
  };

  const onLinkSizeChanged = (linkWidth: number, linkHeight: number, id: K2) => {
    if (linkWidth !== 0 || linkHeight !== 0) {
      const linkDims = linkDimensions.current.get(id);
      if (
        !linkDims ||
        (linkDims &&
          (Math.abs(linkWidth - linkDims.width) > 0.5 ||
            Math.abs(linkHeight - linkDims.height) > 0.5))
      ) {
        linkDimensions.current.set(id, { width: linkWidth, height: linkHeight });
        linkDimsChanged.current = true;
      }
    }
  };

  const onPromotedLinkSizeChanged = (linkWidth: number, linkHeight: number, id: string) => {
    if (linkWidth !== 0 || linkHeight !== 0) {
      const linkDims = promotedLinkDimensions.current.get(id);
      if (
        !linkDims ||
        (linkDims &&
          (Math.abs(linkWidth - linkDims.width) > 0.5 ||
            Math.abs(linkHeight - linkDims.height) > 0.5))
      ) {
        promotedLinkDimensions.current.set(id, { width: linkWidth, height: linkHeight });
        linkDimsChanged.current = true;
      }
    }
  };

  return (
    <div
      ref={rootRef}
      class={outerStyles}
      {...mergedEventProps}
      style={style}
      tabIndex={0}
      role="application"
      aria-label={otherProps['aria-label']}
      aria-labelledby={otherProps['aria-labelledby']}
      aria-describedby={merge([otherProps['aria-describedby'], datatipAriaDescribedby])}
      aria-activedescendant={activeId}>
      {getTextDimensions ? (
        <DiagramPanZoomContainer
          centerX={centerX}
          centerY={centerY}
          zoom={zoomValue}
          width={width}
          height={height}>
          <DiagramLayers
            nodes={diagramNodes}
            links={diagramLinks}
            zoom={zoomValue}
            nodeRenderer={nodeRenderer}
            linkRenderer={linkRenderer}
            width={width}
            height={height}
            nodeBounds={bounds}
            linkDimensions={linkDimensions.current}
            promotedLinkDimensions={promotedLinkDimensions.current}
            states={states.current!}
            onNodeSizeChanged={onNodeSizeChanged}
            onLinkSizeChanged={onLinkSizeChanged}
            onPromotedLinkSizeChanged={onPromotedLinkSizeChanged}
            nodeLabelPositions={nodeLabelPosRef.current}
            linkLabelPositions={linkLabelPosRef.current}
            labelDimensions={labelDimensions.current}
            supportsSelection={
              selectionMode !== 'none' ||
              !!(selectedIds?.nodes && selectedIds.nodes.length > 0) ||
              !!(selectedIds?.links && selectedIds.links.length > 0)
            }
            layoutOutput={layoutOutput}
            nodeIdToParentMap={nodeIdToParentMap}
            rootRef={rootRef}
            combinedNodeBounds={contentBounds}
            nodeBoundsRef={nodeBoundsRef}
            promotedLinks={promotedLinks}
            normalizeLinks={normalizeLinks}
          />
        </DiagramPanZoomContainer>
      ) : undefined}
      {textMeasureContent}
      {datatipContent}
      {contextMenuContent}
      {selectionContent}
      {toggleButtonContent}
    </div>
  );
}
